import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { formatEthSpot, formatRelativeTime } from '@/lib/format'
import { useTokenImage } from '@/lib/projectLinks'
import { subscribeSimLaunchpad } from '@/lib/launchpad/store'
import { subscribeSimPresales } from '@/lib/presale/store'
import {
  loadRecentLaunches,
  loadRecentPresales,
  type RecentKind,
  type RecentToken,
} from '@/lib/recentActivity'
import { onTradeActivityRecorded } from '@/lib/tradeActivity'

type RecentTab = RecentKind

function RecentTokenCard({ item }: { item: RecentToken }) {
  const accent = item.kind === 'launch' ? 'lime' : 'orange'
  const action = item.kind === 'launch' ? 'Bought' : 'Invested'
  const imageUrl = useTokenImage(
    [item.contractAddress, item.shareSlug, item.id, item.tokenAddress],
    item.imageUrl,
  )

  return (
    <Link
      to={item.href}
      className={`group flex items-center gap-3 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-2.5 transition-colors hover:bg-white/[0.05] ${
        accent === 'lime' ? 'hover:border-lime-400/30' : 'hover:border-orange-400/30'
      }`}
    >
      {imageUrl
        ? (
            <img
              src={imageUrl}
              alt={`${item.name} token art`}
              className="h-12 w-12 shrink-0 rounded-xl object-cover sm:h-14 sm:w-14"
            />
          )
        : (
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-sm font-bold sm:h-14 sm:w-14 ${
              accent === 'lime' ? 'bg-lime-400/10 text-lime-400' : 'bg-orange-400/10 text-orange-400'
            }`}
            >
              {item.ticker.slice(0, 2)}
            </div>
          )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-white">{item.name}</p>
        <p className="truncate text-[11px] text-zinc-500">
          $
          {item.ticker}
          {' '}
          ·
          {' '}
          {action}
          {' '}
          {formatEthSpot(item.amountEth)}
        </p>
      </div>
      <p className={`shrink-0 text-[10px] font-medium sm:text-[11px] ${
        accent === 'lime' ? 'text-lime-400' : 'text-orange-400'
      }`}
      >
        {formatRelativeTime(new Date(item.atMs).toISOString())}
      </p>
    </Link>
  )
}

function RecentlyBought() {
  const [tab, setTab] = useState<RecentTab>('launch')
  const [launches, setLaunches] = useState<RecentToken[]>([])
  const [presales, setPresales] = useState<RecentToken[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const refresh = (showLoading: boolean) => {
      if (showLoading)
        setLoading(true)
      Promise.all([loadRecentLaunches(), loadRecentPresales()])
        .then(([nextLaunches, nextPresales]) => {
          if (cancelled)
            return
          setLaunches(nextLaunches)
          setPresales(nextPresales)
        })
        .catch(() => {
          if (cancelled)
            return
          setLaunches([])
          setPresales([])
        })
        .finally(() => {
          if (!cancelled)
            setLoading(false)
        })
    }
    refresh(true)
    const unsubscribeLaunches = subscribeSimLaunchpad(() => refresh(false))
    const unsubscribePresales = subscribeSimPresales(() => refresh(false))
    const unsubscribeTrades = onTradeActivityRecorded(() => refresh(false))
    return () => {
      cancelled = true
      unsubscribeLaunches()
      unsubscribePresales()
      unsubscribeTrades()
    }
  }, [])

  const items = tab === 'launch' ? launches : presales
  const emptyLabel = tab === 'launch'
    ? 'No recent Quick Launch buys at this refresh.'
    : 'No recent Presale investments at this refresh.'

  return (
    <section className="mx-auto max-w-7xl px-4 pt-12 sm:px-6 lg:px-8">
      <div>
        <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${
          tab === 'launch' ? 'text-lime-400' : 'text-orange-400'
        }`}
        >
          Latest activity
        </p>
        <h2 className="mt-1 text-2xl font-bold text-white">Recently Bought</h2>
        <p className="mt-1 text-sm text-zinc-500">
          {tab === 'launch'
            ? 'Most recently bought Quick Launch tokens as of this page refresh.'
            : 'Most recently invested Presale tokens as of this page refresh.'}
        </p>
      </div>

      <div
        className="mt-5 grid grid-cols-2 gap-1 rounded-2xl border border-white/10 bg-white/[0.03] p-1"
        role="tablist"
        aria-label="Recently bought tokens"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'launch'}
          onClick={() => setTab('launch')}
          className={`rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
            tab === 'launch' ? 'bg-lime-400 text-black' : 'text-zinc-400 hover:text-white'
          }`}
        >
          Quick Launches
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'presale'}
          onClick={() => setTab('presale')}
          className={`rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors ${
            tab === 'presale' ? 'bg-orange-400 text-black' : 'text-zinc-400 hover:text-white'
          }`}
        >
          Presales
        </button>
      </div>

      {loading
        ? (
            <p className="mt-5 rounded-2xl border border-dashed border-white/10 px-4 py-8 text-center text-sm text-zinc-500">
              Loading latest buys…
            </p>
          )
        : items.length > 0
          ? (
              <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {items.map(item => (
                  <RecentTokenCard key={`${item.kind}-${item.id}`} item={item} />
                ))}
              </div>
            )
          : (
              <p className="mt-5 rounded-2xl border border-dashed border-white/10 px-4 py-8 text-center text-sm text-zinc-500">
                {emptyLabel}
              </p>
            )}
    </section>
  )
}

export default RecentlyBought
